import React, { useState, useEffect, useRef } from "react";
import { useLoaderData, useNavigate } from "react-router-dom";
import Table from "../components/Table";
import CustomForm from "../components/CustomForm";
import Button from "../components/Button";
import { usHttp2 } from "../custom-hooks/use-http";
import appvariables from "../appvariables";
import { useDispatch, useSelector } from 'react-redux';
import { notificationsliceactions } from "../store/notification";
import { loadingsliceactions } from "../store/loading-slice";

const InventoryManagement = () => {
    const { stocks } = useLoaderData();
    const [inventoryData, setInventoryData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    
    // Form states
    const [selectedProduct, setSelectedProduct] = useState('');
    const [selectedBatch, setSelectedBatch] = useState('');
    const [countToAdd, setCountToAdd] = useState(0);
    const [countError, setCountError] = useState('');
    
    // Popup states
    const [showConfirmationPopup, setShowConfirmationPopup] = useState(false);
    const [showCountedConfirmationPopup, setShowCountedConfirmationPopup] = useState(false);
    const [showExcessItemsPopup, setShowExcessItemsPopup] = useState(false);
    const [pendingBillData, setPendingBillData] = useState([]);
    const [pendingCountedBillData, setPendingCountedBillData] = useState([]);
    const [excessItemsData, setExcessItemsData] = useState([]);
    
    // Form refs
    const productRef = useRef();
    const batchRef = useRef();
    const countRef = useRef();
    
    const navigate = useNavigate();
    const masterdispatcher = useDispatch();
    const bupermissions = useSelector(state => state.budata.permissions);

    // Initialize inventory data with physical count = 0
    useEffect(() => {
        const processedData = stocks.map(stock => ({
            ...stock,
            physicalCount: 0,
            diff: parseInt(stock.Quantity) - 0, // API count - physical count
            uniqueKey: stock.Id, // Use Id as unique key
            diffDisplay: parseInt(stock.Quantity) - 0 // For display with styling
        }));
        setInventoryData(processedData);
        setFilteredData(processedData);
    }, [stocks]);

    // Product options for dropdown (unique products)
    const productOptions = [...new Set(stocks.map(stock => 
        `${stock.Name}---${stock.Package}---${stock.Company}`
    ))];

    // Batch options for selected product
    const batchOptions = selectedProduct ? 
        stocks
            .filter(stock => `${stock.Name}---${stock.Package}---${stock.Company}` === selectedProduct)
            .map(stock => stock.BatchId) : [];

    const TranformDate = (date) => {
        const monthNames = ["January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December"
        ];
        return (monthNames[Number(date.substring(5,7)) - 1]).substring(0,3) + ',' + date.substring(0,4);
    }

    const DiffTransformer = (value) => {
        const bgColor = value > 0 ? '#ffebee' : value < 0 ? '#fff3e0' : '#e8f5e8';
        const textColor = value > 0 ? '#d32f2f' : value < 0 ? '#f57c00' : '#388e3c';
        const prefix = value > 0 ? '+' : '';
        
        return (
            <span style={{
                backgroundColor: bgColor,
                color: textColor,
                padding: '4px 8px',
                borderRadius: '4px',
                fontWeight: 'bold',
                display: 'inline-block',
                minWidth: '40px',
                textAlign: 'center'
            }}>
                {prefix}{value}
            </span>
        );
    }

    const productChangeHandler = () => {
        setSelectedProduct(productRef.current.value);
        setSelectedBatch('');
        batchRef.current.value = '';
    }

    const batchChangeHandler = () => {
        setSelectedBatch(batchRef.current.value);
    }

    const countChangeHandler = () => {
        const value = parseInt(countRef.current.value) || 0;
        setCountToAdd(value);
        if (value <= 0) {
            setCountError('Count must be greater than 0');
        } else {
            setCountError('');
        }
    }

    const refreshStockData = async () => {
        try {
            // Preserve current physical counts before refreshing
            const currentPhysicalCounts = {};
            inventoryData.forEach(item => {
                if (item.physicalCount > 0) {
                    currentPhysicalCounts[item.Id] = item.physicalCount;
                }
            });

            // Fetch latest stock data from API
            const response = await fetch(appvariables.apiurl+'stock/getall/', {
                method: 'POST',
                credentials: "include",
                body: JSON.stringify({})
            });
            const json = await response.json();
            
            if (json.stocks) {
                // Process new stock data and restore physical counts
                const updatedData = json.stocks.map(stock => {
                    const savedPhysicalCount = currentPhysicalCounts[stock.Id] || 0;
                    const newDiff = parseInt(stock.Quantity) - savedPhysicalCount;
                    
                    return {
                        ...stock,
                        physicalCount: savedPhysicalCount,
                        diff: newDiff,
                        uniqueKey: stock.Id, // Use Id as unique key
                        diffDisplay: newDiff
                    };
                });
                
                setInventoryData(updatedData);
                setFilteredData(updatedData);
                
                masterdispatcher(notificationsliceactions.updatenotification({
                    message: 'Stock data refreshed successfully. Physical counts preserved.',
                    status: 'Success'
                }));
            }
        } catch (error) {
            console.error('Error refreshing stock data:', error);
            masterdispatcher(notificationsliceactions.updatenotification({
                message: 'Error refreshing stock data',
                status: 'Error'
            }));
        } finally {
            masterdispatcher(loadingsliceactions.updateloading({'state': ''}));
        }
    }

    const addPhysicalCount = (e) => {
        e.preventDefault();
        
        if (!selectedProduct || !selectedBatch || countToAdd <= 0) {
            setCountError('Please select product, batch and enter valid count');
            return;
        }

        // Find the stock item by matching product and batch
        const matchingItem = inventoryData.find(item => 
            `${item.Name}---${item.Package}---${item.Company}` === selectedProduct && 
            item.BatchId === selectedBatch
        );

        if (!matchingItem) {
            setCountError('Selected product and batch not found');
            return;
        }

        const uniqueKey = matchingItem.Id;
        
        setInventoryData(prevData => {
            const updatedData = prevData.map(item => {
                if (item.uniqueKey === uniqueKey) {
                    const newPhysicalCount = item.physicalCount + countToAdd;
                    const newDiff = parseInt(item.Quantity) - newPhysicalCount;
                    return {
                        ...item,
                        physicalCount: newPhysicalCount,
                        diff: newDiff,
                        diffDisplay: newDiff
                    };
                }
                return item;
            });
            setFilteredData(updatedData);
            return updatedData;
        });

        // Reset form completely and auto-focus product field for quick next entry
        setSelectedProduct('');
        setSelectedBatch('');
        setCountToAdd(0);
        productRef.current.value = '';
        batchRef.current.value = '';
        countRef.current.value = '';
        setCountError('');
        
        // Auto-focus the product field for quick next entry
        if (productRef.current) {
            productRef.current.focus();
        }
    }

    const createAdjustmentBillForMissing = () => {
        // Get all items where difference > 0 (API count > physical count - missing items)
        const missingItems = inventoryData.filter(item => item.diff > 0);
        
        if (missingItems.length === 0) {
            masterdispatcher(notificationsliceactions.updatenotification({
                message: 'No items with positive differences found to create adjustment bill',
                status: 'Success'
            }));
            return;
        }

        masterdispatcher(loadingsliceactions.updateloading({'state': 'loading'}));

        // Prepare bill rows for items with positive differences only
        const billRows = missingItems.map(item => ({
            rowkey: item.Id, // Use Id as rowkey
            name: item.Name,
            package: item.Package,
            company: item.Company,
            batchid: item.BatchId,
            price: parseInt(item.Price),
            expirydate: item.ExpiryDate,
            quantity: item.diff, // The positive difference quantity
            individualpriceenteredbybuser: parseInt(item.Price),
            rowtotal: parseInt(item.Price) * item.diff
        }));

        // Calculate totals
        const totalfrommrp = billRows.reduce((sum, row) => sum + (row.price * row.quantity), 0);
        const totalafterindividualdiscount = billRows.reduce((sum, row) => sum + row.rowtotal, 0);

        const requestbody = {
            customername: 'Others',
            customervillage: 'Others',
            stockdetailjson: billRows,
            totalfrommrp: totalfrommrp,
            totalafterindividualdiscount: totalafterindividualdiscount,
            totalfinaldecided: 1,
            totalrecieved: 1,
            problem: 'NA',
            remarks: `Inventory Adjustment Bill`,
            taskdays: 0,
            taskmessage: '',
            credit: false
        };

        usHttp2(appvariables.apiurl+'bill/create/', 'POST', requestbody)
        .then((response) => response.json())
        .then((json) => {
            masterdispatcher(notificationsliceactions.updatenotification({
                message: `${json.message} - Bill ID: ${json.billid || 'N/A'}`,
                status: json.status
            }));
            
            if (json.status === 'Success' && json.billid) {
                // Refresh stock data while preserving physical counts
                refreshStockData();
                // Don't navigate to bill page - stay in inventory management
            } else {
                masterdispatcher(loadingsliceactions.updateloading({'state': ''}));
            }
        })
        .catch((error) => {
            masterdispatcher(notificationsliceactions.updatenotification({
                message: 'Error creating adjustment bill',
                status: 'Error'
            }));
            masterdispatcher(loadingsliceactions.updateloading({'state': ''}));
        });
    }

    const createAdjustmentBillForCounted = () => {
        // Get counted items with positive differences only (diff > 0)
        const countedItems = inventoryData.filter(item => item.physicalCount > 0 && item.diff > 0);
        
        if (countedItems.length === 0) {
            masterdispatcher(notificationsliceactions.updatenotification({
                message: 'No counted items with positive differences found to create adjustment bill',
                status: 'Success'
            }));
            return;
        }

        // Show confirmation popup with the items
        setPendingCountedBillData(countedItems);
        setShowCountedConfirmationPopup(true);
    }

    const confirmAndCreateCountedBill = () => {
        if (pendingCountedBillData.length === 0) return;

        masterdispatcher(loadingsliceactions.updateloading({'state': 'loading'}));

        // Prepare bill rows for counted items with positive differences only
        const billRows = pendingCountedBillData.map(item => ({
            rowkey: item.Id, // Use Id as rowkey
            name: item.Name,
            package: item.Package,
            company: item.Company,
            batchid: item.BatchId,
            price: parseInt(item.Price),
            expirydate: item.ExpiryDate,
            quantity: item.diff, // The positive difference quantity
            individualpriceenteredbybuser: parseInt(item.Price),
            rowtotal: parseInt(item.Price) * item.diff
        }));

        // Calculate totals
        const totalfrommrp = billRows.reduce((sum, row) => sum + (row.price * row.quantity), 0);
        const totalafterindividualdiscount = billRows.reduce((sum, row) => sum + row.rowtotal, 0);

        const requestbody = {
            customername: 'Others',
            customervillage: 'Others',
            stockdetailjson: billRows,
            totalfrommrp: totalfrommrp,
            totalafterindividualdiscount: totalafterindividualdiscount,
            totalfinaldecided: 1,
            totalrecieved: 1,
            problem: 'NA',
            remarks: `Inventory Adjustment Bill - Counted Items`,
            taskdays: 0,
            taskmessage: '',
            credit: false
        };

        usHttp2(appvariables.apiurl+'bill/create/', 'POST', requestbody)
        .then((response) => response.json())
        .then((json) => {
            masterdispatcher(notificationsliceactions.updatenotification({
                message: `${json.message} - Bill ID: ${json.billid || 'N/A'}`,
                status: json.status
            }));
            
            if (json.status === 'Success' && json.billid) {
                // Refresh stock data while preserving physical counts
                refreshStockData();
            } else {
                masterdispatcher(loadingsliceactions.updateloading({'state': ''}));
            }
            
            // Close popup
            setShowCountedConfirmationPopup(false);
            setPendingCountedBillData([]);
        })
        .catch((error) => {
            masterdispatcher(notificationsliceactions.updatenotification({
                message: 'Error creating adjustment bill for counted items',
                status: 'Error'
            }));
            masterdispatcher(loadingsliceactions.updateloading({'state': ''}));
            setShowCountedConfirmationPopup(false);
            setPendingCountedBillData([]);
        });
    }

    const cancelCountedBillCreation = () => {
        setShowCountedConfirmationPopup(false);
        setPendingCountedBillData([]);
    }

    const createBillForZeroPhysicalCount = () => {
        // Get all items where physical count is 0 and API count > 0 (positive difference)
        const zeroPhysicalItems = inventoryData.filter(item => item.physicalCount === 0 && item.diff > 0);
        
        if (zeroPhysicalItems.length === 0) {
            masterdispatcher(notificationsliceactions.updatenotification({
                message: 'No items with zero physical count found to create adjustment bill',
                status: 'Success'
            }));
            return;
        }

        // Show confirmation popup with the items
        setPendingBillData(zeroPhysicalItems);
        setShowConfirmationPopup(true);
    }

    const confirmAndCreateBill = () => {
        if (pendingBillData.length === 0) return;

        masterdispatcher(loadingsliceactions.updateloading({'state': 'loading'}));

        // Prepare bill rows for items with zero physical count
        const billRows = pendingBillData.map(item => ({
            rowkey: item.Id,
            name: item.Name,
            package: item.Package,
            company: item.Company,
            batchid: item.BatchId,
            price: parseInt(item.Price),
            expirydate: item.ExpiryDate,
            quantity: item.diff, // The positive difference quantity
            individualpriceenteredbybuser: parseInt(item.Price),
            rowtotal: parseInt(item.Price) * item.diff
        }));

        // Calculate totals
        const totalfrommrp = billRows.reduce((sum, row) => sum + (row.price * row.quantity), 0);
        const totalafterindividualdiscount = billRows.reduce((sum, row) => sum + row.rowtotal, 0);

        const requestbody = {
            customername: 'Others',
            customervillage: 'Others',
            stockdetailjson: billRows,
            totalfrommrp: totalfrommrp,
            totalafterindividualdiscount: totalafterindividualdiscount,
            totalfinaldecided: 1,
            totalrecieved: 1,
            problem: 'NA',
            remarks: `Inventory Adjustment Bill - Zero Physical Count Items`,
            taskdays: 0,
            taskmessage: '',
            credit: false
        };

        usHttp2(appvariables.apiurl+'bill/create/', 'POST', requestbody)
        .then((response) => response.json())
        .then((json) => {
            masterdispatcher(notificationsliceactions.updatenotification({
                message: `${json.message} - Bill ID: ${json.billid || 'N/A'}`,
                status: json.status
            }));
            
            if (json.status === 'Success' && json.billid) {
                // Refresh stock data while preserving physical counts
                refreshStockData();
            } else {
                masterdispatcher(loadingsliceactions.updateloading({'state': ''}));
            }
            
            // Close popup
            setShowConfirmationPopup(false);
            setPendingBillData([]);
        })
        .catch((error) => {
            masterdispatcher(notificationsliceactions.updatenotification({
                message: 'Error creating adjustment bill for zero physical count items',
                status: 'Error'
            }));
            masterdispatcher(loadingsliceactions.updateloading({'state': ''}));
            setShowConfirmationPopup(false);
            setPendingBillData([]);
        });
    }

    const cancelBillCreation = () => {
        setShowConfirmationPopup(false);
        setPendingBillData([]);
    }

    const showExcessItemsDetails = () => {
        // Get all items where difference < 0 (physical count > API count - excess items)
        const excessItems = inventoryData.filter(item => item.diff < 0);
        
        if (excessItems.length === 0) {
            masterdispatcher(notificationsliceactions.updatenotification({
                message: 'No excess items found',
                status: 'Success'
            }));
            return;
        }

        // Show popup with excess items details
        setExcessItemsData(excessItems);
        setShowExcessItemsPopup(true);
    }

    const closeExcessItemsPopup = () => {
        setShowExcessItemsPopup(false);
        setExcessItemsData([]);
    }

    const inputs = [
        {
            type: 'text',
            id: 'inventoryproduct',
            refvar: productRef,
            changehandler: productChangeHandler,
            displayname: 'Product',
            name: 'product',
            datalist: productOptions
        },
        {
            type: 'text',
            id: 'inventorybatch',
            refvar: batchRef,
            changehandler: batchChangeHandler,
            displayname: 'Batch ID',
            name: 'batch',
            datalist: batchOptions,
            disabled: !selectedProduct
        },
        {
            type: 'number',
            id: 'inventorycount',
            refvar: countRef,
            changehandler: countChangeHandler,
            displayname: 'Count to Add',
            name: 'count',
            min: 1,
            error: countError
        }
    ];

    const tablefields = [
        {'name': 'Name', 'key':'Name', filter: 1},
        {'name': 'Package', 'key':'Package', filter: 1},
        {'name': 'Company', 'key':'Company', filter: 1},
        {'name': 'Batch ID', 'key':'BatchId', filter: 1},
        {'name': 'API Count', 'key':'Quantity', cs: {width: '100px'}},
        {'name': 'Physical Count', 'key':'physicalCount', cs: {width: '120px'}},
        {'name': 'Difference', 'key':'diff', transformer: DiffTransformer, cs: {width: '100px'}},
        {'name': 'Price', 'key':'Price', cs: {width: '80px'}},
        {'name': 'Expiry', 'key': 'ExpiryDate', transformer: TranformDate, cs: {width: '100px'}},
    ];

    const missingItemsCount = inventoryData.filter(item => item.diff > 0).length;
    const excessItemsCount = inventoryData.filter(item => item.diff < 0).length;
    const matchingItemsCount = inventoryData.filter(item => item.diff === 0).length;
    const countedItemsCount = inventoryData.filter(item => item.physicalCount > 0).length;
    const countedItemsWithPositiveDiffCount = inventoryData.filter(item => item.physicalCount > 0 && item.diff > 0).length;
    const zeroPhysicalCountItems = inventoryData.filter(item => item.physicalCount === 0 && item.diff > 0).length;

    const summaryData = [
        { label: 'Total Items', value: inventoryData.length },
        { label: 'Counted Items', value: countedItemsCount, color: '#9c27b0' },
        { label: 'Missing Items', value: missingItemsCount, color: '#f44336' },
        { label: 'Excess Items', value: excessItemsCount, color: '#ff9800' },
        { label: 'Matching Items', value: matchingItemsCount, color: '#4caf50' }
    ];

    return (
        <div>
            <h2>Inventory Management</h2>
            
            {/* Summary Section */}
            <div style={{
                display: 'flex', 
                gap: '20px', 
                marginBottom: '20px',
                padding: '10px',
                backgroundColor: '#f5f5f5',
                borderRadius: '5px',
                alignItems: 'center'
            }}>
                {summaryData.map((item, index) => (
                    <div key={index} style={{
                        padding: '10px',
                        backgroundColor: 'white',
                        borderRadius: '5px',
                        minWidth: '120px',
                        textAlign: 'center',
                        border: item.color ? `2px solid ${item.color}` : '1px solid #ddd'
                    }}>
                        <div style={{ fontSize: '24px', fontWeight: 'bold', color: item.color || '#333' }}>
                            {item.value}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                            {item.label}
                        </div>
                    </div>
                ))}
                
                {/* Refresh Stock Data Button */}
                <Button 
                    clickhandler={() => {
                        masterdispatcher(loadingsliceactions.updateloading({'state': 'loading'}));
                        refreshStockData();
                    }}
                    cs={{ 
                        backgroundColor: '#ff9800', 
                        borderColor: '#ff9800',
                        padding: '10px 15px',
                        fontSize: '14px'
                    }}
                >
                    Refresh Stock Data
                </Button>
            </div>

            {/* Add Physical Count Form */}
            <div style={{
                backgroundColor: '#f9f9f9',
                padding: '15px',
                borderRadius: '5px',
                marginBottom: '20px'
            }}>
                <h3>Add Physical Count</h3>
                <CustomForm 
                    resetdisabled={true}
                    submitdisabled={!selectedProduct || !selectedBatch || countToAdd <= 0}
                    inputs={inputs}
                    inputboxstyle={'normal'}
                    submitHandler={addPhysicalCount}
                />
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
                <Button 
                    clickhandler={createAdjustmentBillForCounted}
                    disabled={countedItemsWithPositiveDiffCount === 0}
                    cs={{
                        backgroundColor: countedItemsWithPositiveDiffCount === 0 ? 'rgba(0,0,0,0.6)' : '#4caf50',
                        borderColor: countedItemsWithPositiveDiffCount === 0 ? 'rgba(0,0,0,0.6)' : '#4caf50'
                    }}
                >
                    Create Bill for Counted Positive Differences ({countedItemsWithPositiveDiffCount} items)
                </Button>
                
                <Button 
                    clickhandler={createBillForZeroPhysicalCount}
                    disabled={zeroPhysicalCountItems === 0}
                    cs={{
                        backgroundColor: zeroPhysicalCountItems === 0 ? 'rgba(0,0,0,0.6)' : '#9c27b0',
                        borderColor: zeroPhysicalCountItems === 0 ? 'rgba(0,0,0,0.6)' : '#9c27b0'
                    }}
                >
                    Create Bill for Zero Physical Count ({zeroPhysicalCountItems} items)
                </Button>
                
                <Button 
                    clickhandler={showExcessItemsDetails}
                    disabled={excessItemsCount === 0}
                    cs={{
                        backgroundColor: excessItemsCount === 0 ? 'rgba(0,0,0,0.6)' : '#ff9800',
                        borderColor: excessItemsCount === 0 ? 'rgba(0,0,0,0.6)' : '#ff9800'
                    }}
                >
                    Show Excess Items Details ({excessItemsCount} items)
                </Button>
            </div>

            {/* Inventory Table */}
            <Table 
                data={filteredData} 
                tablefields={tablefields}
            />

            {/* Confirmation Popup */}
            {showConfirmationPopup && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        padding: '20px',
                        borderRadius: '10px',
                        maxWidth: '90%',
                        maxHeight: '80%',
                        overflow: 'auto',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                    }}>
                        <h3>Confirm Bill Creation</h3>
                        <p>The following {pendingBillData.length} items with zero physical count will be included in the adjustment bill:</p>
                        
                        <div style={{ marginBottom: '20px', maxHeight: '400px', overflow: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                                <thead>
                                    <tr style={{ backgroundColor: '#f5f5f5' }}>
                                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Name</th>
                                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Package</th>
                                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Company</th>
                                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Batch ID</th>
                                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>API Count</th>
                                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>Difference</th>
                                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>Price</th>
                                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pendingBillData.map((item, index) => (
                                        <tr key={index}>
                                            <td style={{ border: '1px solid #ddd', padding: '8px' }}>{item.Name}</td>
                                            <td style={{ border: '1px solid #ddd', padding: '8px' }}>{item.Package}</td>
                                            <td style={{ border: '1px solid #ddd', padding: '8px' }}>{item.Company}</td>
                                            <td style={{ border: '1px solid #ddd', padding: '8px' }}>{item.BatchId}</td>
                                            <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>{item.Quantity}</td>
                                            <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>{item.diff}</td>
                                            <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>₹{item.Price}</td>
                                            <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>₹{parseInt(item.Price) * item.diff}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div style={{ 
                            borderTop: '1px solid #eee', 
                            paddingTop: '15px',
                            marginBottom: '20px',
                            textAlign: 'right',
                            fontWeight: 'bold'
                        }}>
                            Total Bill Amount: ₹{pendingBillData.reduce((sum, item) => sum + (parseInt(item.Price) * item.diff), 0)}
                        </div>

                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                            <Button 
                                clickhandler={cancelBillCreation}
                                cs={{ 
                                    backgroundColor: '#757575', 
                                    borderColor: '#757575',
                                    padding: '10px 20px'
                                }}
                            >
                                Cancel
                            </Button>
                            <Button 
                                clickhandler={confirmAndCreateBill}
                                cs={{ 
                                    backgroundColor: '#4caf50', 
                                    borderColor: '#4caf50',
                                    padding: '10px 20px'
                                }}
                            >
                                Confirm & Create Bill
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Counted Items Confirmation Popup */}
            {showCountedConfirmationPopup && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        padding: '20px',
                        borderRadius: '10px',
                        maxWidth: '90%',
                        maxHeight: '80%',
                        overflow: 'auto',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                    }}>
                        <h3>Confirm Bill Creation - Counted Items</h3>
                        <p>The following {pendingCountedBillData.length} counted items with positive differences will be included in the adjustment bill:</p>
                        
                        <div style={{ marginBottom: '20px', maxHeight: '400px', overflow: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                                <thead>
                                    <tr style={{ backgroundColor: '#f5f5f5' }}>
                                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Name</th>
                                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Package</th>
                                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Company</th>
                                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Batch ID</th>
                                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>API Count</th>
                                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>Physical Count</th>
                                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>Difference</th>
                                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>Price</th>
                                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {pendingCountedBillData.map((item, index) => (
                                        <tr key={index}>
                                            <td style={{ border: '1px solid #ddd', padding: '8px' }}>{item.Name}</td>
                                            <td style={{ border: '1px solid #ddd', padding: '8px' }}>{item.Package}</td>
                                            <td style={{ border: '1px solid #ddd', padding: '8px' }}>{item.Company}</td>
                                            <td style={{ border: '1px solid #ddd', padding: '8px' }}>{item.BatchId}</td>
                                            <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>{item.Quantity}</td>
                                            <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>{item.physicalCount}</td>
                                            <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>{item.diff}</td>
                                            <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>₹{item.Price}</td>
                                            <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>₹{parseInt(item.Price) * item.diff}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div style={{ 
                            borderTop: '1px solid #eee', 
                            paddingTop: '15px',
                            marginBottom: '20px',
                            textAlign: 'right',
                            fontWeight: 'bold'
                        }}>
                            Total Bill Amount: ₹{pendingCountedBillData.reduce((sum, item) => sum + (parseInt(item.Price) * item.diff), 0)}
                        </div>

                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                            <Button 
                                clickhandler={cancelCountedBillCreation}
                                cs={{ 
                                    backgroundColor: '#757575', 
                                    borderColor: '#757575',
                                    padding: '10px 20px'
                                }}
                            >
                                Cancel
                            </Button>
                            <Button 
                                clickhandler={confirmAndCreateCountedBill}
                                cs={{ 
                                    backgroundColor: '#4caf50', 
                                    borderColor: '#4caf50',
                                    padding: '10px 20px'
                                }}
                            >
                                Confirm & Create Bill
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Excess Items Details Popup */}
            {showExcessItemsPopup && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        padding: '20px',
                        borderRadius: '10px',
                        maxWidth: '90%',
                        maxHeight: '80%',
                        overflow: 'auto',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                    }}>
                        <h3>Excess Items Details</h3>
                        <p>The following {excessItemsData.length} items have excess physical count (more physical count than API count):</p>
                        
                        <div style={{ marginBottom: '20px', maxHeight: '400px', overflow: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                                <thead>
                                    <tr style={{ backgroundColor: '#f5f5f5' }}>
                                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Name</th>
                                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Package</th>
                                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Company</th>
                                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Batch ID</th>
                                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>API Count</th>
                                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>Physical Count</th>
                                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>Excess</th>
                                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>Price</th>
                                        <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>Expiry Date</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {excessItemsData.map((item, index) => (
                                        <tr key={index}>
                                            <td style={{ border: '1px solid #ddd', padding: '8px' }}>{item.Name}</td>
                                            <td style={{ border: '1px solid #ddd', padding: '8px' }}>{item.Package}</td>
                                            <td style={{ border: '1px solid #ddd', padding: '8px' }}>{item.Company}</td>
                                            <td style={{ border: '1px solid #ddd', padding: '8px' }}>{item.BatchId}</td>
                                            <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>{item.Quantity}</td>
                                            <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>{item.physicalCount}</td>
                                            <td style={{ 
                                                border: '1px solid #ddd', 
                                                padding: '8px', 
                                                textAlign: 'right',
                                                backgroundColor: '#fff3e0',
                                                color: '#f57c00',
                                                fontWeight: 'bold'
                                            }}>
                                                {Math.abs(item.diff)}
                                            </td>
                                            <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'right' }}>₹{item.Price}</td>
                                            <td style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'center' }}>{TranformDate(item.ExpiryDate)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div style={{ 
                            borderTop: '1px solid #eee', 
                            paddingTop: '15px',
                            marginBottom: '20px',
                            textAlign: 'right',
                            fontWeight: 'bold',
                            color: '#f57c00'
                        }}>
                            Total Excess Items: {excessItemsData.length} | Total Excess Quantity: {excessItemsData.reduce((sum, item) => sum + Math.abs(item.diff), 0)}
                        </div>

                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                            <Button 
                                clickhandler={closeExcessItemsPopup}
                                cs={{ 
                                    backgroundColor: '#2196f3', 
                                    borderColor: '#2196f3',
                                    padding: '10px 20px'
                                }}
                            >
                                Close
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// Loader function to fetch stock data
export const loadInventoryData = async () => {
    try {
        const response = await fetch(appvariables.apiurl+'stock/getall/', {
            method: 'POST',
            credentials: "include",
            body: JSON.stringify({})
        });
        const json = await response.json();
        return {
            'stocks': json['stocks'] || [],
            'status': json['status'],
            'message': json['message']
        };
    } catch (error) {
        console.error('Error loading inventory data:', error);
        return { stocks: [] };
    }
}

export default InventoryManagement;
